import { useState } from 'react';
import { formatDate } from '../lib/format.js';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import BugReportIcon from '@mui/icons-material/BugReport';
import { IconStat, RankBadge } from '../components/RowStats.js';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';
import { useActiveProject } from '../lib/activeProject.js';

interface FrustratedSession {
  sessionId: string;
  projectId: string;
  projectName: string;
  user: string;
  startedAt: string;
  durationMs: number;
  rageClicks: number;
  deadClicks: number;
  errors: number;
  frustrationScore: number;
}
interface Summary {
  totalSessions: number;
  avgFrustration: number;
  highFrustrationSessions: number;
  rageClicks: number;
  deadClicks: number;
  errors: number;
}
interface Report {
  summary: Summary;
  sessions: FrustratedSession[];
}

const RANGES = [
  { key: 'd7', days: 7 },
  { key: 'd30', days: 30 },
  { key: 'd90', days: 90 },
  { key: 'all', days: 0 },
] as const;

export function FrustrationPage() {
  const all = useT();
  const t = all.signals.frustration;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { selected: projectId } = useActiveProject(orgId);
  const [days, setDays] = useState(30);

  const report = useQuery({
    enabled: !!orgId,
    queryKey: ['frustration', orgId, days, projectId],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '100' });
      if (days) p.set('days', String(days));
      if (projectId) p.set('projectId', projectId);
      return (await api.get(`/v1/orgs/${orgId}/frustration?${p}`)).data as Report;
    },
  });

  const s = report.data?.summary;
  const sort = useSort<'session' | 'frustration' | 'rage' | 'dead' | 'errors' | 'duration' | 'when'>(
    'frustration',
  );
  const rows = sort.sorted(report.data?.sessions ?? [], {
    session: (r) => r.user,
    frustration: (r) => r.frustrationScore,
    rage: (r) => r.rageClicks,
    dead: (r) => r.deadClicks,
    errors: (r) => r.errors,
    duration: (r) => r.durationMs,
    when: (r) => r.startedAt,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalFireDepartmentIcon sx={{ color: '#ef4444' }} />
            <Typography variant="h5">{t.title}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, v) => v != null && setDays(v)}
        >
          {RANGES.map((r) => (
            <ToggleButton key={r.key} value={r.days}>{t.ranges[r.key]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
        <Kpi label={t.kpi.avg} value={`${s?.avgFrustration ?? 0}%`} accent={frustrationColor(s?.avgFrustration ?? 0)} />
        <Kpi label={t.kpi.high} value={s?.highFrustrationSessions ?? 0} accent="#ef4444" sub={t.kpi.highSub} />
        <Kpi label={t.kpi.rage} value={s?.rageClicks ?? 0} accent="#f59e0b" />
        <Kpi label={t.kpi.dead} value={s?.deadClicks ?? 0} accent="#f59e0b" />
        <Kpi label={t.kpi.errors} value={s?.errors ?? 0} accent="#ef4444" />
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        {report.isLoading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {sort.head('session', t.columns.session)}
                {sort.head('frustration', t.columns.frustration)}
                {sort.head('rage', t.columns.rage, 'right')}
                {sort.head('dead', t.columns.dead, 'right')}
                {sort.head('errors', t.columns.errors, 'right')}
                {sort.head('duration', t.columns.duration, 'right')}
                {sort.head('when', t.columns.when)}
                <TableCell align="right">{t.columns.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.sessionId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <RankBadge rank={i + 1} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{r.user}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.projectName}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LinearProgress
                        variant="determinate"
                        value={r.frustrationScore}
                        sx={{ flex: 1, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: frustrationColor(r.frustrationScore) } }}
                      />
                      <Typography variant="caption" fontWeight={700} sx={{ width: 34, textAlign: 'right' }}>
                        {r.frustrationScore}%
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <IconStat icon={<LocalFireDepartmentIcon />} value={r.rageClicks} color="#f59e0b" title={t.stat.rage} />
                  </TableCell>
                  <TableCell align="right">
                    <IconStat icon={<TouchAppIcon />} value={r.deadClicks} color="#0ea5e9" title={t.stat.dead} />
                  </TableCell>
                  <TableCell align="right">
                    <IconStat icon={<BugReportIcon />} value={r.errors} color="#ef4444" title={t.stat.errors} />
                  </TableCell>
                  <TableCell align="right">{Math.round(r.durationMs / 1000)}s</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(r.startedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <RowActions orgId={orgId} row={r} />
                  </TableCell>
                </TableRow>
              ))}
              {!report.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      {t.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

function RowActions({ orgId, row }: { orgId?: string; row: FrustratedSession }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const createTicket = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/v1/orgs/${orgId}/tickets`, {
          title: `Frustrated session, ${row.user}`,
          priority: row.frustrationScore >= 60 ? 'CRITICAL' : 'HIGH',
          projectId: row.projectId,
          sessionId: row.sessionId,
        })
      ).data as { id: string },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['tickets', orgId] });
      navigate(`/tickets/${t.id}`);
    },
  });
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Watch replay">
        <Button
          size="small"
          component={RouterLink}
          to={`/projects/${row.projectId}/sessions/${row.sessionId}`}
          startIcon={<PlayArrowIcon />}
        >
          Watch
        </Button>
      </Tooltip>
      <Tooltip title="Create ticket from this session">
        <Button
          size="small"
          onClick={() => createTicket.mutate()}
          disabled={createTicket.isPending}
          startIcon={<ConfirmationNumberIcon />}
        >
          Ticket
        </Button>
      </Tooltip>
    </Stack>
  );
}

function Kpi({ label, value, accent, sub }: { label: string; value: string | number; accent?: string; sub?: string }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: accent }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>{sub}</Typography>}
    </Paper>
  );
}

function frustrationColor(score: number): string {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  if (score >= 15) return '#eab308';
  return '#2ec2b3';
}
